import React from 'react';
import { motion } from 'framer-motion';

/**
 * Animal information card component
 * Displays animal image, name and description
 * 
 * @param {Object} props
 * @param {Object} props.animal - Animal data object
 * @param {boolean} props.isVisible - Controls whether the card is visible
 */
const AnimalCard = ({ animal, isVisible }) => {
  // If no animal data or card is not visible, don't render content
  if (!animal || !isVisible) return null;

  return (
    <motion.div 
      className="animal-card max-w-3xl mx-auto overflow-hidden"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 0.5,
        type: "spring", 
        stiffness: 100 
      }}
    >
      {/* Animal image area */}
      <motion.div 
        className="relative w-full h-64 sm:h-80 md:h-96 overflow-hidden bg-gray-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
      >
        <img 
          src={animal.image} 
          alt={animal.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </motion.div>
      
      {/* Animal information area */}
      <motion.div 
        className="p-6"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <motion.h2 
          className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 capitalize"
          layout
        >
          {animal.name}
        </motion.h2>
        
        <motion.p 
          className="text-gray-600 mb-4"
          layout
        >
          {animal.description}
        </motion.p>
        
        {/* Additional information area - only shown when data is available */}
        {(animal.habitat || animal.diet || animal.lifespan) && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            {animal.habitat && (
              <div>
                <span className="font-medium text-gray-700">Habitat: </span>
                <span>{animal.habitat}</span>
              </div>
            )}
            
            {animal.diet && (
              <div>
                <span className="font-medium text-gray-700">Diet: </span>
                <span>{animal.diet}</span>
              </div>
            )}
            
            {animal.lifespan && (
              <div>
                <span className="font-medium text-gray-700">Lifespan: </span>
                <span>{animal.lifespan}</span>
              </div>
            )}
          </div>
        )}
      </motion.div>
      
      {/* Action buttons area */}
      <motion.div 
        className="px-6 pb-6 flex flex-wrap gap-3 justify-center sm:justify-start"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <button className="btn-secondary">
          Learn More
        </button>
        <button className="btn-outline">
          Share
        </button>
      </motion.div>
    </motion.div>
  );
};

export default AnimalCard; 